import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {useDevice} from "../components/device/DeviceProvider";
import {useLoading} from "../components/loading/LoadingProvider";
import {
    Form,
    FormControl,
    FormGroup,
    FormLabel,
    Button,
} from "react-bootstrap";
import ErrorsAlert from "../components/alerts/ErrorsAlert";
import {fetchPost, fetchUpload} from "../utils/FetchUtil";
import {img} from "../utils/ImageUtil";
import "../../style/template/egift_message_template_view.scss";
import EventMsgTemplateSwiper from "../components/swiper/EventMsgTemplateSwiper.tsx";
import DesignTemplateSwiper from "../components/swiper/DesignTemplateSwiper.tsx";
import MessageTemplateSwiper from "../components/swiper/MessageTemplateSwiper.tsx";
import EventMsgTemplateModal from "../modal/EventMsgTemplateModal.tsx";

const MAX_CHARS_PER_LINE = 15;
const MAX_LINES = 5;
const MAX_TOTAL_CHARS_WITHOUT_NEWLINES = 75;

interface EGiftMessageTemplateViewProps {
    orderNumber?: string;
    orderDetailNumber?: number;
    handleSaved: () => void;
    handleCanceled: () => void;
}
const EGiftMessageTemplateView: React.FC<EGiftMessageTemplateViewProps> = (
    {
        orderNumber = null,
        orderDetailNumber = null,
        handleSaved = () => {},
        handleCanceled = () => {}
    }
) => {
    const templateKbn: string = "21";
    const { isMobile } = useDevice();
    const { showLoading, hideLoading } = useLoading();
    const [errorList, setErrorList] = useState<string[]>([]);
    const [validated, setValidated] = useState(false);

    const [messageContent, setMessageContent] = useState("");
    const [senderName, setSenderName] = useState("");
    const messageRef = useRef(null);
    const [messageOptionId, setMessageOptionId] = useState(108);
    const [senderOptionId, setSenderOptionId] = useState(106);

    const [selectedCategoryCode, setSelectedCategoryCode] = useState("");
    const [designList, setDesignList] = useState([]);
    const [messageSampleList, setMessageSampleList] = useState([]);
    const [selectedDesign, setSelectedDesign] = useState<GoodsOptionDetailDto>(null);

    const [previewMode, setPreviewMode] = useState<boolean>(false);
    const [showMsgTempModal, setShowMsgTempModal] = useState(false);

    useEffect(() => {
        const getDesignAndMsgTemplateList = async () =>{
            try{
                const data = await fetchPost({
                    action: "/api/template/event/category/design_msg_templates",
                    data: {template_kbn: templateKbn, event_category_code: selectedCategoryCode},
                });
                if(data != null){
                    //イベントカテゴリーメッセージテンプレートを取得
                    setDesignList(data["design_list"]);
                    setMessageSampleList(data["msg_sample_list"]);
                }
            }catch (e) {
                console.log(e);
            }
        }
        getDesignAndMsgTemplateList().finally(()=>{});
    }, [selectedCategoryCode]);

    useEffect( () => {
        const textarea = messageRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const lineCount = (textarea.value.match(/\n/g) || []).length + 1;
            if (lineCount > 10) {
                textarea.style.height = `${textarea.scrollHeight}px`;
            } else {
                textarea.style.height = 'auto';
            }
        }
    }, [messageContent]);
    useEffect( () => {
        if(errorList && errorList.length > 0){
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [errorList]);
    const onClickCancel = ( evt ) => {
        handleCanceled();
    };
    const onClickSave = ( evt ) => {
        setErrorList([]);
        let frmInput = document.getElementById("eGiftMsgInputForm");
        if(frmInput){
            if(frmInput.checkValidity() === false){
                evt.preventDefault();
                evt.stopPropagation();
            }
            setValidated(true);
            if(selectedDesign === null){
                setErrorList(["デザインをご指定ください。"]);
                setValidated(false);
                return;
            }
            const lines = messageContent.split('\n');
            if(lines.length > MAX_LINES){
                setErrorList(["メッセージを1行あたり15文字の5行でご入力ください。"]);
                setValidated(false);
                return;
            }
            for(let i = 0; i < lines.length; i++){
                if(lines[i].length > MAX_CHARS_PER_LINE){
                    setErrorList(["メッセージを1行あたり15文字の5行でご入力ください。"]);
                    setValidated(false);
                    return;
                }
            }
            if(frmInput.checkValidity()){
                entryTemplateAsync().finally(()=>{hideLoading()});
            }
        }
    };

    const entryTemplateAsync = async () => {
        showLoading();
        try{
            const data = await fetchUpload({
                action: '/api/template/entry/apply_site_msg',
                data: createEntryDataJson(),
            });
            if(data !== null){
                handleSaved();
            }
        }catch (err:any){
            console.error('エラー:', err);
            setErrorList(["テンプレートの登録が失敗しました。"]);
        }
    };

    const createEntryDataJson = () => {
        let templateDto = {};
        templateDto["template_kbn"] = templateKbn;
        templateDto["order_number"] = orderNumber;
        templateDto["order_detail_number"] = orderDetailNumber;
        templateDto["template_name"] = "マイデザインオンラインメッセージ（e-gift）";
        let templateDetails = [];
        templateDetails.push({option_id: selectedDesign.option_id, option_detail_id: selectedDesign.option_detail_id});
        templateDetails.push({option_id: messageOptionId, option_detail_id: -1, input_text: messageContent});
        templateDetails.push({option_id: senderOptionId, option_detail_id: -1, input_text: senderName});
        const formData = new FormData();
        formData.append('form_json', JSON.stringify({template: templateDto, detail_list: templateDetails}));
        return formData;
    };

    const onSelectedMsgFromModal = ( msg: string ) => {
        setMessageContent(msg);
        setShowMsgTempModal(false);
    }

    return (
        <section className="mb-5">
            <h2 className="template-title">オンライン・メッセージ登録（無料）</h2>
            <ErrorsAlert error_list={errorList}></ErrorsAlert>
            <Form className="egift-msg-form" id="eGiftMsgInputForm" noValidate validated={validated}>
                <div className="msg-preview-pc">
                    <div className={`input-preview-area`}
                         style={{
                             maxWidth: '600px',
                             minHeight: '450px',
                             backgroundImage: (selectedDesign !== null) ? `url(${img(selectedDesign.image_path01)})` : 'none',
                             backgroundSize: 'cover',
                             backgroundPosition: '0 0',
                             backgroundRepeat: 'no-repeat',
                             boxSizing: 'border-box'}}>
                        <p className="preview-msg" dangerouslySetInnerHTML={{ __html: messageContent.replace(/<br\s*\/?>/g, '\n').replace(/\n/g, '<br />')}}></p>
                        <p className="preview-sender">{senderName}</p>
                    </div>
                </div>
                <div className={`msg-input-pc`}>
                    <h4 className="input-subject">1.ご利用するテンプレートをお選びください。</h4>
                    <div className="design-message-templates">
                        <EventMsgTemplateSwiper templateKbn="21" showTemplate={false} onSelectedCategory={(code:string)=>{setSelectedCategoryCode(code)}}></EventMsgTemplateSwiper>
                        <h5>デザインを指定してください</h5>
                        <DesignTemplateSwiper templateList={designList} selectedTemplateDto={selectedDesign} addClassName={`event-design-swiper`} onSelectedChange={(dto) => {setSelectedDesign(dto)}}></DesignTemplateSwiper>
                        <h5>メッセージを選択してください</h5>
                        <MessageTemplateSwiper templateList={messageSampleList} onSelectedMessage={(msg) => {setMessageContent(msg)}}></MessageTemplateSwiper>
                    </div>
                    <h4 className="input-subject">2. メッセージのご入力。</h4>
                    <FormGroup className="mb-3">
                        <FormLabel style={{width: '100%'}}>メッセージ<span className="text-danger">　※1行あたり15文字の5行</span><span className="float-end">{messageContent.replace(/\n/g, '').length}/{MAX_TOTAL_CHARS_WITHOUT_NEWLINES}</span></FormLabel>
                        <FormControl as="textarea" required
                                     id="txtMessage"
                                     style={{ overflowY: 'hidden', resize: 'none' }}
                                     ref={messageRef}
                                     rows={MAX_LINES}
                                     placeholder="メッセージをご入力ください"
                                     value={messageContent}
                                     onChange={(e) => {setMessageContent(e.target.value)}}
                        ></FormControl>
                        <Form.Control.Feedback type="invalid">
                            メッセージを入力してください。
                        </Form.Control.Feedback>
                    </FormGroup>
                    <FormGroup className="mb-3">
                        <FormLabel style={{width: '100%'}}>お名前<span className="float-end">{senderName.length}/{MAX_TOTAL_CHARS_WITHOUT_NEWLINES}</span></FormLabel>
                        <FormControl type="text" placeholder="お名前をご入力ください" maxLength={15} required value={senderName} onChange={(e) => setSenderName(e.target.value)}></FormControl>
                        <Form.Control.Feedback type="invalid">
                            お名前を入力してください。
                        </Form.Control.Feedback>
                    </FormGroup>
                </div>
                <div className={`msg-input-sp`}>
                    <h4 className="input-subject">1.ご利用するテンプレートをお選びください。</h4>
                    <EventMsgTemplateSwiper templateKbn="21" showTemplate={false} onSelectedCategory={(code:string)=>{setSelectedCategoryCode(code)}}></EventMsgTemplateSwiper>
                    <DesignTemplateSwiper templateList={designList} selectedTemplateDto={selectedDesign} addClassName={`event-design-swiper`} onSelectedChange={(dto) => {setSelectedDesign(dto)}}></DesignTemplateSwiper>
                    <h4 className="input-subject mt-3">2. メッセージのご入力。</h4>
                    <div className="msg-input-buttons">
                        <div>
                            {!previewMode && <>
                                <Button variant="success" onClick={() => {setShowMsgTempModal(true)}}>メッセージテンプレ</Button>
                            </>}
                        </div>
                        {!previewMode && <Button variant="secondary" style={{backgroundColor: '#269494', minWidth: '11rem'}} onClick={() => {setPreviewMode(true)}}>
                            プレビュー
                            <span className="float-end-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                                    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                                </svg>
                            </span>
                        </Button>}
                        {previewMode && <Button variant="secondary" style={{backgroundColor: '#AB6F06', minWidth: '11rem'}} onClick={() => setPreviewMode(false)}>
                            編　集
                            <span className="float-end-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
                                </svg>
                            </span>
                        </Button>}
                    </div>
                    <div className={`egift-msg-area`}
                         style={{backgroundImage: (selectedDesign !== null) ? `url(${img(selectedDesign.image_path01)})` : 'none'}}
                    >
                        {previewMode && <>
                            <p className="preview-msg"
                               style={{marginTop: '1.5rem' }}
                               dangerouslySetInnerHTML={{ __html: messageContent.replace(/<br\s*\/?>/g, '\n').replace(/\n/g, '<br />')}}
                            ></p>
                            <p className="preview-sender">{senderName}</p>
                        </>}
                        {!previewMode && <>
                            <FormGroup className="mb-3" style={{width: '100%', opacity: '.7', marginTop: '1.5rem' }}>
                                <FormControl as="textarea" required
                                             id="txtMessage"
                                             style={{ overflowY: 'hidden', resize: 'none', textAlign: 'center', color: 'black' }}
                                             ref={messageRef}
                                             rows={MAX_LINES}
                                             placeholder="メッセージをご入力ください"
                                             value={messageContent}
                                             onChange={(e) => {setMessageContent(e.target.value)}}
                                ></FormControl>
                                <Form.Control.Feedback type="invalid">
                                    メッセージを入力してください。
                                </Form.Control.Feedback>
                            </FormGroup>
                            <FormGroup className="mb-3" style={{width: '100%', opacity: '.7' }}>
                                <FormControl type="text" required
                                             style={{textAlign: 'center', color: 'black' }}
                                             placeholder="お名前をご入力ください"
                                             maxLength={15}
                                             value={senderName} onChange={(e) => setSenderName(e.target.value)}
                                ></FormControl>
                                <Form.Control.Feedback type="invalid">
                                    お名前を入力してください。
                                </Form.Control.Feedback>
                            </FormGroup>
                        </>}
                    </div>
                </div>
            </Form>
            <div className="buttons-area">
                <Button variant="light" onClick={onClickCancel}>戻る</Button>
                <Button variant="secondary" onClick={onClickSave}>保　存</Button>
            </div>
            {isMobile && <div>
                <EventMsgTemplateModal templateKbn="21" show={showMsgTempModal} onClosed={() => {setShowMsgTempModal(false)}} onSelected={onSelectedMsgFromModal}></EventMsgTemplateModal>
            </div>}
        </section>
    );
};
export default EGiftMessageTemplateView;